/**
 * Display formatting for the figures the API serves.
 *
 * One module so the storyboard's two date shapes — the as-of chip's
 * `9 Aug 2026` and the mission board's `Oct '26` — are written once and read
 * the same on every page. Everything here is a pure string transform: nothing
 * decides what a number *means*, which is `bands.ts`'s job alone (rule #3).
 *
 * en-GB throughout, because the storyboard is en-GB and a locale-sensitive
 * dashboard would render a different date to the projector than to the PDF.
 */

const LOCALE = 'en-GB';

const DAY_FORMAT = new Intl.DateTimeFormat(LOCALE, {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

const MONTH_FORMAT = new Intl.DateTimeFormat(LOCALE, { month: 'short', timeZone: 'UTC' });

const YEAR_FORMAT = new Intl.DateTimeFormat(LOCALE, { year: 'numeric', timeZone: 'UTC' });

const FIGURE_FORMAT = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 1 });

/**
 * Parse an API date (`2026-08-09`) or timestamp (`2026-08-09T23:59:00Z`).
 *
 * UTC throughout. A bare `YYYY-MM-DD` is already parsed as UTC midnight by
 * spec; forcing the formatter to UTC as well stops a machine in UTC+8 from
 * rendering the fortnight's as-of date as the following morning.
 */
function parse(iso: string): Date | null {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** `2026-08-09` → `9 Aug 2026`. The as-of chip and every observed-at line. */
export function formatDay(iso: string | null | undefined): string | null {
  if (iso == null || iso === '') return null;
  const date = parse(iso);
  return date === null ? null : DAY_FORMAT.format(date);
}

/**
 * `2026-10-30` → `Oct '26`. The storyboard's milestone date shape, which
 * deliberately drops the day: a milestone lands in a month, not on a Tuesday.
 *
 * Clipped to three letters because en-GB abbreviates September as `Sept`,
 * which is correct English and wrong for a fixed-width board column — the
 * storyboard's own row reads `Sep '26`.
 */
export function formatMonthYear(iso: string | null | undefined): string | null {
  if (iso == null || iso === '') return null;
  const date = parse(iso);
  if (date === null) return null;
  const month = MONTH_FORMAT.format(date).slice(0, 3);
  return `${month} '${YEAR_FORMAT.format(date).slice(-2)}`;
}

/**
 * A measured figure as the meeting reads it: `212`, `58`, `1.4`. Trailing
 * zeros are dropped because the API sends every value as a float and
 * `212.0 q/day` reads like a precision claim nobody made.
 */
export function formatFigure(value: number | null | undefined): string | null {
  if (value == null || !Number.isFinite(value)) return null;
  return FIGURE_FORMAT.format(value);
}

/** A percentage for a bar read-out: `42%`. */
export function formatPercent(value: number | null | undefined): string | null {
  const figure = formatFigure(value);
  return figure === null ? null : `${figure}%`;
}

/** The direction glyph that makes a delta legible without its colour (rule #4). */
export function deltaGlyph(delta: number | null | undefined): string {
  if (delta == null || !Number.isFinite(delta) || delta === 0) return '=';
  return delta > 0 ? '▲' : '▼';
}

/** `23` → `+23`, `-4` → `-4`, `0` → `0`. The storyboard's delta chip. */
export function formatSignedDelta(delta: number | null | undefined): string | null {
  const figure = formatFigure(delta);
  if (figure === null) return null;
  return delta != null && delta > 0 ? `+${figure}` : figure;
}
